import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { CalendarDays, Clock, Users, MapPin, ChevronDown, Plus, Minus, Locate } from 'lucide-react';
import { formatDateDisplay, formatTimeDisplay } from '../../utils/planningHelpers';
import { formatDateShort, detectZipFromIP } from '../../utils/smartDefaults';
import { DURATION_OPTIONS } from '../../utils/planningConstants';

export default function BookingBar({ hub }) {
  const { eventDetails, setEventDetails, suggestedDates,
    datePopoverOpen, setDatePopoverOpen, timePopoverOpen, setTimePopoverOpen,
    guestsPopoverOpen, setGuestsPopoverOpen, locationPopoverOpen, setLocationPopoverOpen } = hub;

  return (
    <div className="bg-card border rounded-xl shadow-lg mb-6 sm:mb-8 overflow-hidden" data-testid="booking-bar">
      <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 divide-x divide-border">
        {/* Date */}
        <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 hover:bg-muted/50 transition-colors text-left w-full" data-testid="booking-bar-date">
              <CalendarDays className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Date</p>
                <p className="font-medium text-xs sm:text-sm truncate">{formatDateDisplay(eventDetails.date)}</p>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-4" align="start">
            <div className="space-y-3">
              <Label>Event Date</Label>
              <Input type="date" value={eventDetails.date} onChange={(e) => setEventDetails({ ...eventDetails, date: e.target.value })} min={new Date().toISOString().split('T')[0]} />
              {suggestedDates.length > 0 && (
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1.5">Popular Upcoming Dates</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {suggestedDates.map(sd => (
                      <button key={sd.date} onClick={() => setEventDetails({ ...eventDetails, date: sd.date })}
                        className={`text-left p-2 rounded-lg border text-xs transition-all ${eventDetails.date === sd.date ? 'bg-indigo-50 border-indigo-300 text-indigo-700 dark:bg-indigo-500/10 dark:border-indigo-500/30 dark:text-indigo-400' : 'border-border hover:border-indigo-200'}`}
                        data-testid={`quick-date-${sd.date}`}>
                        <p className="font-medium truncate">{sd.label}</p>
                        <p className="text-[10px] text-muted-foreground">{formatDateShort(sd.date)}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <Button size="sm" className="w-full" onClick={() => setDatePopoverOpen(false)}>Done</Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Time */}
        <Popover open={timePopoverOpen} onOpenChange={setTimePopoverOpen}>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 hover:bg-muted/50 transition-colors text-left w-full" data-testid="booking-bar-time">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Time</p>
                <p className="font-medium text-xs sm:text-sm truncate">{formatTimeDisplay(eventDetails.time, eventDetails.duration)}</p>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-4" align="start">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input type="time" value={eventDetails.time} onChange={(e) => setEventDetails({ ...eventDetails, time: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Duration</Label>
                <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {DURATION_OPTIONS.map(opt => (
                    <Button key={opt.hours} variant={eventDetails.duration === opt.hours ? 'default' : 'outline'} size="sm" onClick={() => setEventDetails({ ...eventDetails, duration: opt.hours })} className="text-xs">
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>
              <Button size="sm" className="w-full" onClick={() => setTimePopoverOpen(false)}>Done</Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Guests */}
        <Popover open={guestsPopoverOpen} onOpenChange={setGuestsPopoverOpen}>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 hover:bg-muted/50 transition-colors text-left w-full" data-testid="booking-bar-guests">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Guests</p>
                <p className="font-medium text-xs sm:text-sm">{eventDetails.guestCount} people</p>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="start">
            <div className="space-y-4">
              <Label>Number of Guests</Label>
              <div className="flex items-center gap-4">
                <Button variant="outline" size="icon" onClick={() => setEventDetails({ ...eventDetails, guestCount: Math.max(1, eventDetails.guestCount - 10) })}><Minus className="h-4 w-4" /></Button>
                <Input type="number" value={eventDetails.guestCount} onChange={(e) => setEventDetails({ ...eventDetails, guestCount: Math.max(1, parseInt(e.target.value) || 1) })} className="text-center" min="1" />
                <Button variant="outline" size="icon" onClick={() => setEventDetails({ ...eventDetails, guestCount: Math.min(1000, eventDetails.guestCount + 10) })}><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {[25, 50, 100, 150, 200, 300].map(num => (
                  <Button key={num} variant={eventDetails.guestCount === num ? 'default' : 'outline'} size="sm" onClick={() => setEventDetails({ ...eventDetails, guestCount: num })} className="text-xs">{num}</Button>
                ))}
              </div>
              <Button size="sm" className="w-full" onClick={() => setGuestsPopoverOpen(false)}>Done</Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Location */}
        <Popover open={locationPopoverOpen} onOpenChange={setLocationPopoverOpen}>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 hover:bg-muted/50 transition-colors text-left w-full" data-testid="booking-bar-location">
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Location</p>
                <p className="font-medium text-xs sm:text-sm truncate">{eventDetails.venueZipCode ? `ZIP: ${eventDetails.venueZipCode}` : 'Detecting...'}</p>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-4" align="end">
            <div className="space-y-3">
              <Label>Venue ZIP Code</Label>
              <div className="relative">
                <Input type="text" value={eventDetails.venueZipCode} onChange={(e) => setEventDetails({ ...eventDetails, venueZipCode: e.target.value })} placeholder="Enter ZIP code" maxLength={10} />
                <button onClick={() => detectZipFromIP().then(z => z && setEventDetails(p => ({ ...p, venueZipCode: z })))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-indigo-500 hover:text-indigo-600 p-1" title="Auto-detect from location">
                  <Locate className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Auto-detected from your location</p>
              <Button size="sm" className="w-full" onClick={() => setLocationPopoverOpen(false)}>Done</Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
